/**
 * Cameras Live workstream rail.
 * Continuous stream of events, AI team work, dispatch and thinking — not framed chat cards.
 * Honesty: Workstream · POLL
 */
import { html, M } from "./ui.js";
import { cameraLabelSay, looksLikeCode, operatorSay } from "./operator-say.js";

function textOf(...values) {
  for (const value of values) {
    const text = typeof value === "string" ? value.trim() : "";
    if (text) return text;
  }
  return "";
}

function objectOf(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function epochMs(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed : Date.now();
}

const EVENT_TITLES = Object.freeze({
  "audit.udock.session.open": "Live session opened",
  "audit.udock.session.close": "Live session closed",
  "udock.session.open": "Live session opened",
  "udock.session.close": "Live session closed",
  "ai.enrich.verify_camera": "AI checked this lens",
  "snapshot.captured": "Snapshot kept",
  "camera.snapshot.captured": "Snapshot kept",
});

function looksLikeWireKey(value) {
  const text = String(value || "").trim();
  if (/^[a-z][a-z0-9]+([._][a-z0-9]+)+$/i.test(text)) return true;
  return /^(audit|udock|camera|ai|snapshot)\.[a-z0-9_.]+/i.test(text);
}

function looksLikeEnvelopeReason(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (looksLikeWireKey(text)) return true;
  return /^(live_session_|awaiting_|catalogued_|binding_|no_|session_not_)/i.test(text);
}

/** Map API wall summaries / trigger lines. Old strings stay in comments for honesty. */
function humanizeCameraLine(raw) {
  let s = String(raw || "").trim();
  if (!s) return "";
  /* Honesty (not painted): unknown trigger · stamped snapshot(s) · stamped evidence */
  s = s.replace(/\bNo live session and no stamped evidence on this source\.?/gi,
    "No live view and no kept picture yet.");
  s = s.replace(/\bLive session open — no stamped snapshot yet\.?/gi,
    "Live view is open · no picture kept yet.");
  s = s.replace(/\bLive session with (\d+) stamped snapshot\(s\)\.?/gi,
    "Live view open · $1 picture(s) kept.");
  s = s.replace(/\b(\d+) stamped snapshot\(s\); lens not live\.?/gi,
    "$1 picture(s) kept · live view is off.");
  s = s.replace(/\bunknown trigger\b/gi, "Trigger not named yet");
  s = s.replace(/\bai trigger\b/gi, "AI kept this picture");
  s = s.replace(/\bhuman trigger\b/gi, "Someone kept this picture");
  s = s.replace(/\balarm trigger\b/gi, "Alarm kept this picture");
  s = s.replace(/\borigin not reported\b/gi, "source not named");
  s = s.replace(/\bgoverned_ai_requested_this_lens\b/gi, "AI asked for this camera");
  s = s.replace(/\bregistry_visibility_only\b/gi, "On the camera list only");
  s = s.replace(/\balarm\.trigger\b/gi, "alarm");
  s = s.replace(/·\s*\d{3,}\s*$/g, "").trim();
  if (looksLikeWireKey(s)) return operatorSay(s, "Camera update");
  return s;
}

function stripWireTokens(value) {
  return String(value || "")
    .replace(/\b(?:audit\.)?udock\.session\.\w+/gi, "")
    .replace(/\bsystem:ai_autonomy\b/gi, "")
    .replace(/\bCROC-[A-Z0-9]+\b/g, "")
    .replace(/\bcam-[a-f0-9]{8,}\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function actorLabelOf(value) {
  const raw = textOf(value);
  if (!raw) return "";
  if (raw === "system:ai_autonomy" || raw === "governed_ai") return "AI team";
  if (raw.startsWith("system:")) return "System";
  return raw;
}

export function operatorTitleOf(raw, kind = "signal") {
  const key = String(raw || "").trim();
  if (!key) return tagOf(kind);
  const mapped = EVENT_TITLES[key.toLowerCase()];
  if (mapped) return mapped;
  if (/udock\.session\.open/i.test(key)) return "Live session opened";
  if (/udock\.session\.close/i.test(key)) return "Live session closed";
  if (/udock\.session/i.test(key)) return "Live session";
  if (/snapshot/i.test(key) && /captur|keep|taken/i.test(key)) return "Snapshot kept";
  if (looksLikeWireKey(key)) {
    return operatorSay(key, tagOf(kind));
  }
  if (looksLikeCode(key)) {
    return operatorSay(key, "This camera");
  }
  return operatorSay(key, key);
}

function detailOf(row, payload) {
  const candidates = [
    row.summary,
    payload.summary,
    typeof row.detail === "string" ? row.detail : "",
    payload.message,
    payload.reason,
    payload.note,
    row.message,
    row.line,
  ];
  for (const candidate of candidates) {
    const text = stripWireTokens(candidate);
    if (text && !looksLikeWireKey(text) && !looksLikeEnvelopeReason(text)) {
      return humanizeCameraLine(text);
    }
  }
  const actor = actorLabelOf(row.actor || payload.actor || row.actor_username);
  if (actor) return actor;
  return "";
}

function kindOf(row) {
  const raw = textOf(row.kind, row.event_type, row.type, row.action, row.role).toLowerCase();
  if (/(think|reason|scoring|rank|estimate)/.test(raw)) return "think";
  if (/(team|agent|discuss|debate|vote)/.test(raw)) return "team";
  if (/(dispatch|assign|ack|en_route|loop|responder)/.test(raw)) return "dispatch";
  if (/(action|command|open|close|capture|fix)/.test(raw)) return "action";
  if (/(ai|model|enrich|decision|read_note|stamp|select|detect)/.test(raw)) return "ai";
  return "signal";
}

function tagOf(kind) {
  if (kind === "think") return "THINK";
  if (kind === "team") return "TEAM";
  if (kind === "dispatch") return "DISPATCH";
  if (kind === "action") return "ACTION";
  if (kind === "ai") return "AI";
  return "EVENT";
}

export function normalizeCameraSignal(row, fallbackIndex = 0) {
  if (!row || typeof row !== "object") return null;
  const payload = objectOf(row.payload || row.detail);
  const nested = typeof row.detail === "object" && row.detail ? row.detail : payload;
  const sourceId = textOf(
    row.source_id,
    row.device_id,
    row.subject_ref,
    nested.source_id,
    nested.device_id,
    nested.subject_ref,
  );
  const kind = kindOf(row);
  const at = epochMs(row.at || row.created_at || row.ts || row.ts_epoch_ms);
  const id = textOf(row.id, row.event_id, nested.event_id)
    || `${sourceId || "event"}:${kind}:${at}:${fallbackIndex}`;
  const title = humanizeCameraLine(operatorTitleOf(
    textOf(row.title, nested.title, row.event_type, row.type, row.action, nested.event_type),
    kind,
  ));
  const control = actorLabelOf(
    textOf(
      row.control,
      row.actor,
      row.actor_username,
      nested.actor,
      nested.author,
    ),
  );
  return {
    id,
    source_id: sourceId,
    kind,
    title,
    detail: detailOf(row, nested),
    control,
    tone: textOf(row.tone, kind),
    at,
    raw: row,
  };
}

/** Project live card situations (AI actions, dispatch loops) into the stream. */
export function projectCardLiveWork(cards) {
  const out = [];
  for (const card of Array.isArray(cards) ? cards : []) {
    if (!card || typeof card !== "object") continue;
    const sit = objectOf(card.situation);
    const sourceId = textOf(card.source_id);
    const label = textOf(card.label, sourceId);
    const actions = Array.isArray(sit.ai_actions) ? sit.ai_actions : [];
    for (const act of actions) {
      if (!act || typeof act !== "object") continue;
      out.push({
        id: `ai:${sourceId}:${textOf(act.id, act.action, act.at)}`,
        kind: textOf(act.kind, act.action, "ai"),
        title: textOf(act.action, act.kind, "AI step"),
        detail: textOf(act.detail, act.summary, act.note),
        control: textOf(act.actor, "AI team"),
        source_id: sourceId,
        at: epochMs(act.at),
      });
    }
    const loop = objectOf(sit.dispatch_loop);
    if (loop.id) {
      out.push({
        id: `loop:${loop.id}:${sourceId}`,
        kind: "dispatch",
        title: "A person is on the way",
        detail: textOf(
          loop.dispatch_reason,
          sit.dispatch && sit.dispatch.summary,
          `${textOf(loop.state, "open")} · ${textOf(loop.responder, loop.assignee, "unassigned")}`,
        ),
        control: textOf(loop.responder, loop.assignee, "dispatch"),
        source_id: sourceId,
        at: epochMs(loop.responded_at || loop.opened_at || card.pulse_at),
      });
      const stages = Array.isArray(loop.stages) ? loop.stages : [];
      for (const stage of stages) {
        if (!stage || !stage.done) continue;
        out.push({
          id: `stage:${loop.id}:${textOf(stage.id)}`,
          kind: "dispatch",
          title: textOf(stage.label, stage.id),
          detail: `${label} · stage complete`,
          control: textOf(loop.responder, "dispatch"),
          source_id: sourceId,
          at: epochMs(card.pulse_at) - 1000,
        });
      }
    }
    const why = textOf(sit.why);
    if (why && !looksLikeEnvelopeReason(why)) {
      out.push({
        id: `why:${sourceId}`,
        kind: "think",
        title: "AI reading",
        detail: why,
        control: textOf(objectOf(sit.control).label, "AI"),
        source_id: sourceId,
        at: epochMs(card.pulse_at),
      });
    }
    const selection = textOf(sit.selection);
    if (selection && !looksLikeEnvelopeReason(selection)) {
      out.push({
        id: `select:${sourceId}`,
        kind: "team",
        title: "Team selection",
        detail: selection,
        control: "agent team",
        source_id: sourceId,
        at: epochMs(card.pulse_at) - 500,
      });
    }
    const evidence = Array.isArray(sit.evidence) ? sit.evidence : [];
    for (const row of evidence) {
      if (!row || typeof row !== "object") continue;
      const note = textOf(row.note);
      const aiRead = textOf(row.ai_read);
      if (!note && !aiRead) continue;
      out.push({
        id: `media:${textOf(row.snapshot_id)}`,
        kind: note ? "dispatch" : "ai",
        title: note ? "Field media + reason" : "AI media read",
        detail: note
          ? `${note}${aiRead ? ` · AI: ${aiRead}` : ""}`
          : aiRead,
        control: textOf(row.author, row.trigger_origin, "media"),
        source_id: sourceId,
        at: epochMs(row.at),
      });
    }
  }
  return out;
}

export function buildCameraSignalFeed(wallActivity, eventItems, cards) {
  const map = new Map();
  const rows = [
    ...(Array.isArray(wallActivity) ? wallActivity : []),
    ...(Array.isArray(eventItems) ? eventItems : []),
    ...projectCardLiveWork(cards),
  ];
  rows.forEach((row, index) => {
    const signal = normalizeCameraSignal(row, index);
    if (!signal || map.has(signal.id)) return;
    if (!textOf(signal.title) && !textOf(signal.detail)) return;
    if (signal.title === "Event received" && !textOf(signal.detail)) return;
    map.set(signal.id, signal);
  });
  return [...map.values()]
    .sort((a, b) => Number(b.at || 0) - Number(a.at || 0))
    .slice(0, 120);
}

function clockOf(at) {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(new Date(Number(at || Date.now())));
  } catch {
    return "—";
  }
}

export function CameraSignalRail({ signals, transport, scopeLabel, selectedSourceId }) {
  const rows = Array.isArray(signals) ? signals : [];
  return html`<${M.aside} className="cw-signal" data-mode="stream" aria-label="Live workstream"
      initial=${{ opacity: 0, x: -18 }} animate=${{ opacity: 1, x: 0 }}
      transition=${{ duration: 0.34, ease: [.16, 1, .3, 1] }}>
    <header className="cw-signal__head">
      <div>
        <h2>What's happening</h2>
        <p>${scopeLabel || "All cameras"}</p>
      </div>
      <span data-live=${transport === "sse" ? "1" : "0"}>
        <i/>${transport === "sse" ? "LIVE" : "Updating"}
      </span>
    </header>
    <div className="cw-signal__tape" role="log" aria-live="polite">
      ${rows.length ? rows.map((row, index) => html`<${M.div}
          key=${row.id}
          className="cw-signal__line"
          data-kind=${row.kind}
          data-source=${row.source_id === selectedSourceId ? "selected" : ""}
          initial=${{ opacity: 0, y: 4 }}
          animate=${{ opacity: 1, y: 0 }}
          transition=${{ duration: 0.18, delay: Math.min(index * 0.012, 0.12) }}>
        <div className="cw-signal__meta">
          <time>${clockOf(row.at)}</time>
          <em>${tagOf(row.kind)}</em>
        </div>
        <p>
          <b>${row.kind === "lens" ? cameraLabelSay(row.title) : operatorSay(row.title, row.title)}</b>
          <span>${row.detail}</span>
        </p>
      <//>`) : html`<div className="cw-signal__empty">
        <i/>
        <b>Waiting</b>
        <span>AI, dispatch and events stream here.</span>
      </div>`}
    </div>
  <//>`;
}
